import * as angular from "angular";
import {apsEnums} from "../../shared/dto/aps.enums";
import HttpServiceName, {HttpService} from "../../services/http/http.service";
import HubServiceName, {HubService} from "../../services/hubs/hub.service";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import template from "./diagnostics.tpl.html";
import {SignalRHub} from "../../services/hubs/signalr-hub";
import {Endpoints, Strings} from "../../shared/config/constant.module";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import {apsCommands} from "../../shared/dto/aps.commands";
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import UIUtilityServiceName, {EnumOption, UIUtilityService} from "../../services/utility/uiUtility.service";
import EquipmentDiagnosticsDetailName, {openEquipmentDiagnosticsDetail} from "./diagnosticsDetail.controller";

let requires = [HttpServiceName, HubServiceName, LoaderServiceName, NotifyServiceName, UIUtilityServiceName,
  EquipmentDiagnosticsDetailName];

interface DiagnosticsController {
  logLevels: EnumOption[];
  name: string;
  logLevel: apsEnums.LogLevel;
  events: apsViewModels.EquipmentDiagnostics[];
  maxRows: number;

  levelChanged(): void;
  maxRowsChanged(): void;
  formatTime(time: Date): string;
  logLevelName(logLevel: apsEnums.LogLevel): string;
  viewEvent(e: apsViewModels.EquipmentDiagnostics): void;
  truncateMessage(message: string): string
  close(): void;
}

interface EquipmentHub extends SignalRHub {
  joinGroup(plantId: string, instanceId: string): Promise<any>;
  leaveGroup(plantId: string, instanceId: string): Promise<any>;
}

export interface EquipmentDiagnosticsParams {
  equipment: apsViewModels.EquipmentDetail;
  plantId: string;
  timeZoneId: string;
}

export function openEquipmentDiagnostics(
  data: EquipmentDiagnosticsParams,
  $modal: mm.foundation.modal
): angular.IPromise<any> {
  return $modal.open({
    template: template,
    controller: 'EquipmentDiagnosticsCtrl as vm',
    resolve: {
      data: () => data
    }
  }).result;
}

export default angular.module('aps.controller.equipmentDiagnostics', requires)
  .controller('EquipmentDiagnosticsCtrl', diagnosticsCtrl)
  .name;

function logLevelName(logLevel: apsEnums.LogLevel): string {
  return apsEnums.LogLevel[logLevel];
}

function truncateMessage(message: string): string {
  if (message && message.length > 75) {
    return message.substring(0, 75) + '...';
  }
  return message;
}

function diagnosticsCtrl(
  httpService: HttpService,
  hubService: HubService,
  data: EquipmentDiagnosticsParams,
  $scope: angular.IScope,
  $timeout: angular.ITimeoutService,
  $modal: mm.foundation.modal,
  $modalInstance: mm.foundation.modalInstance,
  $q: angular.IQService,
  uiUtilityService: UIUtilityService,
  loaderService: LoaderService,
  notifyService: NotifyService
): DiagnosticsController {
  let vm = {} as DiagnosticsController;
  let equipment = data.equipment;
  let hub: EquipmentHub;

  vm.name = equipment.name;
  vm.events = [];
  vm.logLevel = equipment.diagnosticsLogLevel;
  vm.logLevels = uiUtilityService.getListOptionsFromEnum(apsEnums.LogLevel, 1);
  vm.maxRows = equipment.maxLogEntries;

  function formatTime(time: Date): string {
    return DateUtilityService.formatDateTime(time, data.timeZoneId, Strings.TIME_FORMAT_WITH_SECONDS);
  }
  vm.formatTime = formatTime;

  vm.logLevelName = logLevelName;
  vm.truncateMessage = truncateMessage;

  function diagnosticsLogged(e: apsViewModels.EquipmentDiagnostics): void {
    if (e.equipmentInstanceId !== equipment.instanceId) {
      return;
    }
    $timeout(() => {
      addIndexToEvent(e);
      vm.events.unshift(e);
      while (vm.events.length > vm.maxRows) {
        vm.events.pop();
      }
    });
  }

  let index = 0;
  function addIndexToEvent(event: any): void {
    event.index = index;
    index++;
  }

  function createHub(): EquipmentHub {
    return hubService.createHub<EquipmentHub>('equipment', {
      listeners: {
        diagnosticsLogged: diagnosticsLogged
      },
      methods: ['joinGroup', 'leaveGroup'],
      postConnect: function(hub: EquipmentHub) {
        hub.invokeWithRetry(function() {
          return hub.joinGroup(data.plantId, equipment.instanceId);
        });
      }
    });
  }

  function getLogs(): angular.IPromise<any> {
    return httpService.get<apsViewModels.EquipmentDiagnostics[]>(
      Endpoints.EQUIPMENT_DIAGNOSTICS, {
        plantId: data.plantId,
        instanceId: equipment.instanceId
      }).then(logs => {
        logs.forEach(addIndexToEvent);
        vm.events = logs;
        hub = createHub();
      });
  }

  function changeLogLevel(): angular.IPromise<any> {
    let command: apsCommands.UpdateEquipmentDiagnosticsLevel = {
      plantId: data.plantId,
      id: equipment.id,
      diagnosticsLogLevel: vm.logLevel,
      maxLogEntries: vm.maxRows || equipment.maxLogEntries
    };
    return httpService.put(Endpoints.EQUIPMENT_DIAGNOSTICS, command)
      .then(() => {
        if (!vm.logLevel && equipment.diagnosticsLogLevel) {
          hub.leaveGroup(data.plantId, equipment.instanceId);
        } else if (vm.logLevel && !equipment.diagnosticsLogLevel) {
          hub.joinGroup(data.plantId, equipment.instanceId);
        }
        equipment.diagnosticsLogLevel = vm.logLevel;
        equipment.maxLogEntries = vm.maxRows;
      }, errors => {
        vm.logLevel = equipment.diagnosticsLogLevel;
        vm.maxRows = equipment.maxLogEntries;
        notifyService.handleApiErrors(errors);
      });
  }

  function logLevelChanged(): void {
    let promise: angular.IPromise<any>;
    if (vm.logLevel && !hub) {
      promise = getLogs().then(changeLogLevel);
    } else {
      promise = changeLogLevel();
    }
    loaderService.loadingPromise(promise);
  }
  vm.levelChanged = logLevelChanged;

  function maxRowsChanged(): void {
    if (this.form.$invalid) {
      return;
    }

    loaderService.loadingPromise(changeLogLevel());
  }
  vm.maxRowsChanged = maxRowsChanged;

  function viewEvent(e: apsViewModels.EquipmentDiagnostics) {
    openEquipmentDiagnosticsDetail({
      name: vm.name,
      time: formatTime(e.eventDate),
      logLevel: logLevelName(e.logLevel),
      message: e.message
    }, $modal);
  }
  vm.viewEvent = viewEvent;

  function preClose(): angular.IPromise<boolean> {
    if (!vm.logLevel || vm.logLevel > apsEnums.LogLevel.Debug) {
      return $q.when(true);
    }

    return uiUtilityService.confirm('You currently have logging set to '
      + logLevelName(vm.logLevel)
      + '. This will impact performance at the plant. Are you sure you want to leave it on?', 'Leave Logging On?');
  }

  function close() {
    preClose().then(function(confirmed) {
      if (confirmed) {
        $modalInstance.close();
      }
    });
  }
  vm.close = close;

  if (vm.logLevel) {
    loaderService.loadingPromise(getLogs());
  }

  $scope.$on('$destroy', () => {
    if (hub) {
      hub.leaveGroup(data.plantId, equipment.instanceId)
        .then(() => hub.uninitialize());
    }
  });

  return vm;
}


// WEBPACK FOOTER //
// ./src/app/controllers/equipment/diagnostics.controller.ts